import React, {ReactElement} from 'react';
import {Typography} from '@mui/material';
import {CampaignAttributes} from 'models/storeDetails';
import {useGetOrderSummaryQuery} from '../../api/storesApi';
import OrderStatusBar from '.';
import './index.scss';

type ProfitProgressBarProps = {
  storeSlug: string;
  primaryCampaign: CampaignAttributes;
  children?: JSX.Element | JSX.Element[];
};

type SupportHeaderProps = {
  totalSupporters: number;
};

const SupportHeader: React.FC<SupportHeaderProps> = ({
  totalSupporters
}: SupportHeaderProps): ReactElement => {
  return (
    // PENDING: Remove fontSize prop override with Pigment update
    <Typography
      variant="h4"
      fontSize={20}
      className="tracker-progressBar-status">
      {`${totalSupporters} ${
        totalSupporters === 1 ? 'supporter' : 'supporters'
      }`}
    </Typography>
  );
};

const progressBarPercentage = (goal: number, totalProfit: number): number => {
  if (!totalProfit || !goal) return 0;
  const progressPercent = (totalProfit / goal) * 100;

  return Math.min(progressPercent, 100);
};

const ProfitProgressBar: React.FC<ProfitProgressBarProps> = ({
  storeSlug,
  primaryCampaign,
  children
}: ProfitProgressBarProps): ReactElement => {
  const {data: campaignOrderData} = useGetOrderSummaryQuery({
    urlSlug: storeSlug,
    campaignId: primaryCampaign?.id
  });
  const storeProfit = campaignOrderData?.profit;

  const goal = storeProfit?.goal || 0;
  const totalProfit = storeProfit?.totalProfit || 0;
  const totalSupporters = storeProfit?.totalSupporters;

  return storeProfit ? (
    <div className="tracker-progressBar">
      <OrderStatusBar
        header={<SupportHeader totalSupporters={totalSupporters || 0} />}
        percentage={progressBarPercentage(goal, totalProfit)}>
        <Typography variant="captionSmall" fontSize={14}>
          {totalProfit ? `$${totalProfit.toLocaleString()}` : '$0.00'}{' '}
          <span className="goalAmount">{`/ $${goal.toLocaleString()} goal`}</span>
        </Typography>
      </OrderStatusBar>
      <div className="content">{children}</div>
    </div>
  ) : null;
};

ProfitProgressBar.defaultProps = {
  children: undefined
};

export default ProfitProgressBar;
